import { type ReactNode } from 'react'

import type { AppContext } from '../../auth/session.js'
import type { SidebarNavItem } from '../../design-system/index.js'

// The role-aware information architecture, as data. Each entry maps a nav item
// to its route. The tenant nav is the client workspace; the admin nav is the
// INHERTA operator console, filtered by platform role (ADMIN_ONLY_NAV). Since
// B1–B3 both sides hold the full toolset — bot-config lives under /admin AND
// the owner edits their own agent under /asistente; "concierge" means we CAN
// drive everything for a client, not that only we can.

function I({ d }: { d: string }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  )
}

const ICONS = {
  home: 'M3 11.5 12 4l9 7.5M5 10v9.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10',
  inbox:
    'M3 12h5l2 3h4l2-3h5M5 5h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
  worker:
    'M12 8V4M8 8h8a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2ZM9 13h.01M15 13h.01M2 14h2M20 14h2',
  leads: 'M4 19V5m5 14V9m5 10v-6m5 6V7',
  oversight: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  reports: 'M4 19h16M7 16v-5m5 5V8m5 8v-3',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 13a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 0 1-4 0v-.1A1.7 1.7 0 0 0 7 19.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H3a2 2 0 0 1 0-4h.1A1.7 1.7 0 0 0 4.6 7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 2.9-1.2V3a2 2 0 0 1 4 0v.1A1.7 1.7 0 0 0 17 4.6l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z',
  grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  clients:
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  card: 'M2 7h20M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1ZM6 15h4',
  template: 'M4 5h16v4H4zM4 13h7v6H4zM15 13h5v6h-5z',
  handoff: 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21v-1a6 6 0 0 1 6-6h1M15 17l2 2 4-4',
  receipt: 'M6 2h12v20l-3-2-3 2-3-2-3 2V2ZM9 7h6M9 11h6M9 15h4',
  managers:
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M19 8v6M22 11h-6',
  users:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  agenda:
    'M4 6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6ZM4 10h16M8 3v4M16 3v4',
  catalog: 'M3 11V5a2 2 0 0 1 2-2h6l10 10-8 8L3 11ZM7.5 7.5h.01',
  contacts: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 20a8 8 0 0 1 16 0',
  messages: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z',
} as const

export interface NavEntry extends SidebarNavItem {
  path: string
  /** Owner/admin-only surface — hidden from agents/viewers (UI hint; the API
   *  still enforces the writes). */
  manageOnly?: boolean
}

export const TENANT_NAV: NavEntry[] = [
  { id: 'home', label: 'Inicio', path: '/home', icon: <I d={ICONS.home} /> },
  { id: 'inbox', label: 'Bandeja', path: '/bandeja', icon: <I d={ICONS.inbox} /> },
  {
    id: 'oversight',
    label: 'Supervisión',
    path: '/supervision',
    icon: <I d={ICONS.oversight} />,
    manageOnly: true,
  },
  {
    id: 'worker',
    label: 'Agentes',
    path: '/asistente',
    icon: <I d={ICONS.worker} />,
    manageOnly: true,
  },
  // The board is where you work ANY process, hence the label. `id` and `path`
  // are FROZEN identifiers (bookmarks + e2e routes) and do not follow the copy.
  { id: 'leads', label: 'Procesos', path: '/prospectos', icon: <I d={ICONS.leads} /> },
  { id: 'contacts', label: 'Contactos', path: '/contactos', icon: <I d={ICONS.contacts} /> },
  // C3: every role sees the agenda (Asesores live by the day's citas); the
  // page hides write controls for read-only roles and the API gates writes.
  { id: 'agenda', label: 'Agenda', path: '/agenda', icon: <I d={ICONS.agenda} /> },
  {
    id: 'catalog',
    label: 'Catálogo',
    path: '/catalogo',
    icon: <I d={ICONS.catalog} />,
    manageOnly: true,
  },
  // Manage-only: a report is the scoreboard on the people who work the inbox
  // (per-agent throughput, cost, SLA), and the API now gates GET /reports on
  // `reports.read` (owner/admin). Leaving it in an Asesor's sidebar would be a
  // door onto a 403.
  {
    id: 'reports',
    label: 'Reportes',
    path: '/reportes',
    icon: <I d={ICONS.reports} />,
    manageOnly: true,
  },
  // Manage-only: "Soporte" is the concierge thread between the workspace and
  // its INHERTA account manager, and every /concierge endpoint requires
  // `tenant.concierge` (owner/admin only). An Asesor clicking it got a 403 and
  // an empty page — the definition of a painted door. Their support path is
  // their own owner/admin, not our account manager.
  {
    id: 'messages',
    label: 'Soporte',
    path: '/mensajes',
    icon: <I d={ICONS.messages} />,
    manageOnly: true,
  },
  {
    id: 'settings',
    label: 'Configuración',
    path: '/configuracion',
    icon: <I d={ICONS.settings} />,
    manageOnly: true,
  },
]

export const ADMIN_NAV: NavEntry[] = [
  { id: 'overview', label: 'Resumen', path: '/admin', icon: <I d={ICONS.grid} /> },
  { id: 'tenants', label: 'Clientes', path: '/admin/tenants', icon: <I d={ICONS.clients} /> },
  {
    id: 'admin-messages',
    label: 'Mensajes',
    path: '/admin/mensajes',
    icon: <I d={ICONS.messages} />,
  },
  {
    id: 'managers',
    label: 'Gerentes de cuenta',
    path: '/admin/account-managers',
    icon: <I d={ICONS.managers} />,
  },
  {
    id: 'users',
    label: 'Usuarios',
    path: '/admin/usuarios',
    icon: <I d={ICONS.users} />,
  },
  {
    id: 'handoffs',
    label: 'Con personas',
    path: '/admin/handoffs',
    icon: <I d={ICONS.handoff} />,
  },
  {
    id: 'subscriptions',
    label: 'Suscripciones',
    path: '/admin/subscriptions',
    icon: <I d={ICONS.card} />,
  },
  {
    id: 'cfdi',
    label: 'Facturación fiscal',
    path: '/admin/cfdi',
    icon: <I d={ICONS.receipt} />,
  },
  {
    id: 'templates',
    label: 'Plantillas de vertical',
    path: '/admin/templates',
    icon: <I d={ICONS.template} />,
  },
]

// Nav ids that only make sense for healthcare-shaped verticals. Agenda
// (Doctores/Citas) and Catálogo (Estudios/servicios) carry medical vocabulary
// and workflows that don't apply to casas de empeño, dealerships, etc. Only
// salud is built out end-to-end, so hide them for other verticals.
const HEALTHCARE_ONLY_NAV = new Set(['agenda', 'catalog'])
const HEALTHCARE_VERTICALS = new Set(['healthcare', 'doctor'])

/** ¿Este espacio TIENE Agenda y Catálogo? Es el mismo hecho con el que se filtra
 *  la barra lateral, exportado porque desde D21-1 hay una segunda pantalla que
 *  lo necesita: el diálogo que apunta un dato a una de esas dos fuentes no puede
 *  ofrecer como origen una pantalla que este giro no tiene. Dos respuestas a
 *  "¿existe la Agenda aquí?" serían una de más. Vertical indefinido (operador, o
 *  un token de antes del claim) sigue siendo compatible hacia atrás y ve las dos.
 *  Pista de UI, no autorización: el API manda. */
export function isHealthcareVertical(vertical?: string): boolean {
  return vertical === undefined || HEALTHCARE_VERTICALS.has(vertical)
}

/** Admin-nav ids that only platform_admin/platform_owner work with: the money
 *  surfaces (Suscripciones, Facturación fiscal) and org management (Gerentes).
 *  Operators and AMs keep the operational set — a nav entry whose page
 *  refuses you is a door painted on a wall. UI hint only, not authz:
 *  the routes keep their own guards. */
const ADMIN_ONLY_NAV = new Set(['managers', 'users', 'subscriptions', 'cfdi'])

/**
 * The sidebar entries a session gets: admin nav (money surfaces only for
 * platform_admin/owner) or tenant nav filtered by manage permission and
 * vertical (healthcare-only surfaces hidden elsewhere). UI hint only — every
 * route keeps its own guard.
 */
export function navForContext(
  context: AppContext,
  canManageWorkspace = true,
  vertical?: string,
  platformRole?: string,
): NavEntry[] {
  if (context === 'admin') {
    const isAdmin = platformRole === 'platform_admin' || platformRole === 'platform_owner'
    return isAdmin ? ADMIN_NAV : ADMIN_NAV.filter((n) => !ADMIN_ONLY_NAV.has(n.id))
  }
  // Agents/viewers don't manage the workspace → hide the admin-only surfaces.
  const roleScoped = canManageWorkspace ? TENANT_NAV : TENANT_NAV.filter((n) => !n.manageOnly)
  // Hide healthcare-shaped surfaces for non-salud verticals. Undefined vertical
  // (operator or a token minted before the claim existed) stays backward
  // compatible and shows them.
  const isHealthcare = isHealthcareVertical(vertical)
  return isHealthcare ? roleScoped : roleScoped.filter((n) => !HEALTHCARE_ONLY_NAV.has(n.id))
}

/** The nav id whose path best matches the current location. */
export function activeIdForPath(nav: NavEntry[], pathname: string): string {
  const match = nav
    .filter((n) => pathname === n.path || pathname.startsWith(`${n.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]
  return match?.id ?? nav[0]?.id ?? ''
}
